import { MenuItem } from "@kaltura/ds-react-components";
import { useState } from "react";
import UpdateMediaFoldersModal, {
    UpdateMediaFoldersModalProps,
} from "./update-media-folders-modal/UpdateMediaFoldersModal";
import { ButtonClickAnalyticsType, FilterValues } from "@kaltura/mediaspace-shared-types";
import { useFoldersStore } from "../foldersStore";
import { ButtonAnalyticsProvider, useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";

export interface UpdateMediaFoldersProps extends UpdateMediaFoldersModalProps {
    /** menu item label */
    label: string;
    /** currently selected folder id in the search filters */
    selectedFolderId?: string;
    /** currently selected filter values */
    filterValues?: FilterValues;
    /** button name for reporting menu item click analytics  */
    buttonAnalyticsActionName?: string;
}

/**
 * update media folders menu item
 * used in:
 * my-media single entry action, my-media bulk action
 */
export function UpdateMediaFolders({
    label,
    onCloseMenu,
    entryIds,
    selectedFolderId,
    reloadMedia,
    filterValues,
    buttonAnalyticsActionName,
    ...menuItemProps
}: UpdateMediaFoldersProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const isLoadingFolders = useFoldersStore((state) => state.isLoadingFolders);

    const sendButtonAnalytics = useButtonAnalytics();

    // current folder id is derived from:
    // selectedFolderId prop - for single entry action, coming from the server
    // filterValues.folderId - for bulk entries action, coming from the my-media app
    const currentFolderId =
        selectedFolderId ?? (filterValues && typeof filterValues.folderId === "string" ? filterValues.folderId : "");

    const openMediaFoldersModal = () => {
        setIsModalOpen(true);
        buttonAnalyticsActionName
            && sendButtonAnalytics(
                buttonAnalyticsActionName,
                entryIds.length === 1 ? ButtonClickAnalyticsType.EDIT : ButtonClickAnalyticsType.ADD,
                entryIds.length === 1 ? entryIds[0] : undefined,
                "folders"
            );
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleClose = () => {
        handleCloseModal();
        onCloseMenu?.();
    };

    return (
        <>
            <MenuItem
                key={"update-media-folders"}
                onClick={openMediaFoldersModal}
                disabled={isLoadingFolders}
                {...menuItemProps}
            >
                {label}
            </MenuItem>
            {isModalOpen && (
                <ButtonAnalyticsProvider location={"Modal - Add/remove from folders"} applicationFeature={"folders"}>
                    <UpdateMediaFoldersModal
                        isOpen={isModalOpen}
                        entryIds={entryIds}
                        currentFolderId={currentFolderId}
                        onClose={handleCloseModal}
                        reloadMedia={reloadMedia}
                        onCloseMenu={handleClose}
                    />
                </ButtonAnalyticsProvider>
            )}
        </>
    );
}

export default UpdateMediaFolders;
